/**
 * Prequalification step definitions and sessionStorage persistence.
 * STEPS is the single source of truth for field order, question text,
 * and valid options. The Zod schema drives type extraction and validation.
 */

import { NETWORK_MATCH, NETWORK_TYPE, PTZ_COUNT, prequalSchema } from '#~/analytics/index.js';
import type { PrequalificationAnswers } from '#~/analytics/index.js';
import { isObject } from '@tryiris-ai/js-types';

export { prequalSchema, type PrequalificationAnswers };

// ─── Steps ───

interface StepBase<F extends keyof PrequalificationAnswers> {
  readonly field: F;
  readonly title: string;
  readonly subtitle: string;
  readonly options: readonly {
    readonly label: string;
    readonly value: PrequalificationAnswers[F];
  }[];
}

export type StepConfig = {
  [F in keyof PrequalificationAnswers]: StepBase<F>;
}[keyof PrequalificationAnswers];

export const STEPS: readonly StepConfig[] = [
  {
    // Per ENG-4171 — count range replaces the old yes/no/unsure availability
    // question; labels use en-dashes (1–3) but values are ASCII (1-3) to match
    // the schema literals on PTZ_COUNT.
    field: 'ptzCount',
    title: 'How many PTZ cameras do you have?',
    subtitle: "We'll use this to personalize your experience.",
    options: [
      { label: '0', value: PTZ_COUNT['0'] },
      { label: '1–3', value: PTZ_COUNT['1-3'] },
      { label: '4–7', value: PTZ_COUNT['4-7'] },
      { label: '8+', value: PTZ_COUNT['8+'] }
    ]
  },
  {
    field: 'networkMatch',
    title: 'Are you on the same network as your cameras?',
    subtitle: 'This helps us check if Iris can discover your cameras.',
    options: [
      { label: 'Yes', value: NETWORK_MATCH.yes },
      { label: 'No', value: NETWORK_MATCH.no },
      { label: "I'm not sure", value: NETWORK_MATCH.unsure }
    ]
  },
  {
    field: 'networkType',
    title: 'Are you on a home or enterprise network?',
    subtitle: 'This helps us recommend the right setup.',
    options: [
      { label: 'Home', value: NETWORK_TYPE.home },
      { label: 'Enterprise', value: NETWORK_TYPE.enterprise }
    ]
  }
  // Per ENG-4171 — the platform (desktop/mobile) question was removed; STEPS is
  // now 3 entries and TOTAL_STEPS (= STEPS.length) updates automatically.
];

export const TOTAL_STEPS = STEPS.length;

// ─── Storage ───

export const PREQUAL_STORAGE_KEY = '@prequal.responses';

/** Saves partial or complete responses to sessionStorage. Never throws. */
export function savePrequalResponses(responses: Partial<PrequalificationAnswers>): void {
  try {
    window.sessionStorage.setItem(PREQUAL_STORAGE_KEY, JSON.stringify(responses));
  } catch (error) {
    console.warn('[prequal-storage] Failed to save responses:', error);
  }
}

/** Loads responses from sessionStorage. Keeps individually valid fields, drops the rest. */
export function loadPrequalResponses(): Partial<PrequalificationAnswers> | null {
  try {
    const raw = window.sessionStorage.getItem(PREQUAL_STORAGE_KEY);
    if (raw === null) return null;
    const json: unknown = JSON.parse(raw);

    const full = prequalSchema.safeParse(json);
    if (full.success) return full.data;

    if (!isObject<Record<string, unknown>>(json)) return {};
    const result: Partial<PrequalificationAnswers> = {};
    for (const step of STEPS) {
      const parsed = prequalSchema.shape[step.field].safeParse(json[step.field]);
      if (parsed.success) {
        Object.assign(result, { [step.field]: parsed.data });
      }
    }
    return result;
  } catch (error) {
    console.warn('[prequal-storage] Failed to load responses:', error);
    return null;
  }
}

/** Removes responses from sessionStorage. */
export function clearPrequalResponses(): void {
  try {
    window.sessionStorage.removeItem(PREQUAL_STORAGE_KEY);
  } catch (error) {
    console.warn('[prequal-storage] Failed to clear responses:', error);
  }
}

// ─── Completion / resume ───

/** Type guard: all required prequalification fields are present and valid. */
export function isPrequalComplete(responses: unknown): responses is PrequalificationAnswers {
  return prequalSchema.safeParse(responses).success;
}

/** Returns the 0-based index of the first unanswered/invalid step, or TOTAL_STEPS if complete. */
export function deriveStartStep(responses: unknown): number {
  if (!isObject<Record<string, unknown>>(responses)) return 0;
  for (let i = 0; i < STEPS.length; i++) {
    const step = STEPS[i];
    if (step === undefined) return i;
    if (
      !Object.hasOwn(responses, step.field) ||
      !prequalSchema.shape[step.field].safeParse(responses[step.field]).success
    ) {
      return i;
    }
  }
  return STEPS.length;
}
